import { useI18N, useResponsive } from "@sundae/react-hooks";
import { TabsV3, Text, toolkitCx } from "@sundae/ui-toolkit";
import cx from "classnames";
import isEmpty from "lodash/isEmpty";
import { FC, memo, useCallback, useMemo, useState } from "react";
import { joyrideSelectors } from "../../../constants/joyride.constants";
import { TransactionType } from "../../../gql/generated/taste-test.sdk";
import { useHeaderHeight } from "../../../hooks/useHeaderHeight";
import { useSticky } from "../../../hooks/useSticky";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { EOrderFlowState, useOrderContext } from "../../../stores/OrderContext";
import { safariSafeDate } from "../../../utils/dates.utils";
import TasteTestClaimedView from "../TasteTestClaimedView";
import TasteTestClaimingFlow from "../TasteTestClaimingFlow";
import TasteTestContributionsTable from "../TasteTestContributionsTable";
import TasteTestCurrentPositionWidget from "../TasteTestCurrentPositionWidget";
import TasteTestDepositFlow from "../TasteTestDepositFlow";
import TasteTestKPIsMarquee from "../TasteTestKPIsMarquee";
import TasteTestMissedOut from "../TasteTestMissedOut";
import TasteTestPendingOrderView from "../TasteTestPendingOrderView";
import TasteTestWithdrawFlow from "../TasteTestWithdrawFlow";

const {
  theme: { colors },
} = require("@sundae/tailwind-config");
const defaultPrimaryColor = colors.primary["DEFAULT"];

const TasteTestTransactionWidget: FC = () => {
  const { t } = useI18N("taste-test");
  const {
    state: {
      flowData: { flowState },
    },
  } = useOrderContext();
  const {
    derived: { hasContribution },
    handlers: { resetTasteTestState },
    state: {
      actionType,
      persistedContributionsHistory,
      showCurrentContribution,
      tasteTest,
      tasteTestStatus: { isTasteTestConcluded, isTasteTestInactive },
      tasteTestPersonalization,
      tasteTestHistory,
    },
  } = useTasteTestContext();
  const { headerHeight } = useHeaderHeight();
  const [stickyRef, isSticky] = useSticky<HTMLDivElement>();
  const { lg } = useResponsive();
  const [tabValue, setTabValue] = useState<"widget" | "contributionsTable">("widget");
  const isStickyOnLargeScreen = isSticky && lg;

  const isPendingOrderForTasteTest = persistedContributionsHistory?.some(
    ({ id }) => id === tasteTest?.policyId
  );
  const showPendingOrderView =
    !isEmpty(persistedContributionsHistory) &&
    isPendingOrderForTasteTest &&
    flowState !== EOrderFlowState.success;

  const claimTx = tasteTestHistory?.find((tx) => tx.type === TransactionType.Claim);
  const showClaimingView = useMemo(
    () => hasContribution && isTasteTestConcluded && !claimTx,
    [claimTx, tasteTestHistory, isTasteTestConcluded]
  );
  const showClaimedView = useMemo(
    () => isTasteTestConcluded && !!claimTx,
    [claimTx, tasteTestHistory, isTasteTestConcluded]
  );
  const showCurrentPositionWidgetView =
    !isTasteTestInactive &&
    hasContribution &&
    showCurrentContribution &&
    [EOrderFlowState.reset, EOrderFlowState.initial, EOrderFlowState.previewCancelled].includes(
      flowState
    );

  /**
   * This flag determines whether to show the missed out node or not.
   * We want to wait _two hours_ after the taste test concluded to show the missed out node.
   * This is to have a smooth transition from the "Inactive" state to the "Missed Out" state.
   */
  const renderMissedOutNode = useMemo(() => {
    return false;
    const now = new Date();
    const endDate = new Date(safariSafeDate(tasteTest?.endDate.format) || "");
    const twoHoursAfterEndDate = new Date(endDate.getTime() + 2 * 60 * 60 * 1000);
    const isTwoHoursAfterEndDate = now >= twoHoursAfterEndDate;
    const hasClaimableTx = hasContribution && !!claimTx;
    return isTwoHoursAfterEndDate && isTasteTestConcluded && !hasClaimableTx;
  }, [claimTx, hasContribution, tasteTest?.endDate.format, isTasteTestConcluded]);

  const handleTabChange = useCallback(
    (value: string) => {
      setTabValue(value as "widget" | "contributionsTable");
      resetTasteTestState(!showPendingOrderView); // If we have a pending order, do not clear the information.
    },
    [showPendingOrderView]
  );

  const memoizedTransactionTabTriggerLabel = useMemo(() => {
    if (showClaimingView) {
      return t("detail.contributionsTab.widget", {
        context: "claiming",
      });
    }
    if (showClaimedView) {
      return t("detail.contributionsTab.widget", {
        context: "claimed",
      });
    }
    if (showCurrentPositionWidgetView) {
      return t("detail.contributionsTab.widget", {
        context: "withContribution",
      });
    }

    return t("detail.contributionsTab.widget");
  }, [showCurrentPositionWidgetView, showClaimedView, showClaimingView, t]);

  const memoizedWidgetNode = useMemo(() => {
    if (showPendingOrderView) {
      return <TasteTestPendingOrderView isSticky={isStickyOnLargeScreen} />;
    }

    if (showClaimingView) {
      return <TasteTestClaimingFlow isSticky={isStickyOnLargeScreen} />;
    }

    if (showClaimedView) {
      return <TasteTestClaimedView isSticky={isStickyOnLargeScreen} />;
    }

    if (showCurrentPositionWidgetView) {
      return <TasteTestCurrentPositionWidget isSticky={isStickyOnLargeScreen} />;
    }

    if (actionType === "withdraw") {
      return <TasteTestWithdrawFlow isSticky={isStickyOnLargeScreen} />;
    }

    return <TasteTestDepositFlow isSticky={isStickyOnLargeScreen} />;
  }, [
    actionType,
    flowState,
    isStickyOnLargeScreen,
    showCurrentPositionWidgetView,
    showPendingOrderView,
    showClaimingView,
    showClaimedView,
    tasteTestHistory,
  ]);

  return (
    <div
      ref={stickyRef}
      className={cx("flex h-fit flex-col gap-4", {
        "relative w-full self-start": !lg,
        sticky: lg, // Apply sticky positioning only on large screens
      })}
      // Apply top positioning only on large screens
      style={{
        top: lg ? headerHeight : undefined,
        zIndex: lg ? 10 : undefined,
      }}
    >
      <div
        data-guide-selector={joyrideSelectors.guides.tasteTest.interactiveGuide.transactionWidget}
      >
        <div className="relative flex flex-col">
          {/* -------------------------------------------------------------------------------------------------
           * Marquee with KPIs
           * -----------------------------------------------------------------------------------------------*/}
          {lg && (
            <div
              className={cx(
                "absolute left-0 top-1 w-full select-none",
                {
                  "opacity-100": isSticky,
                  "opacity-0": !isSticky,
                },
                toolkitCx.layout.transition.opacity
              )}
            >
              <TasteTestKPIsMarquee />
            </div>
          )}
          <div
            className="relative flex flex-col gap-4"
            style={{ color: tasteTestPersonalization?.primaryColor || defaultPrimaryColor }}
          >
            <div
              className={cx(
                "absolute inset-0 scale-110 rounded-full opacity-20 blur-[48px] dark:opacity-10",
                {
                  "bg-primary dark:bg-secondary": !tasteTestPersonalization?.primaryColor,
                  "bg-current": tasteTestPersonalization?.primaryColor,
                }
              )}
            />

            {renderMissedOutNode ? (
              <TasteTestMissedOut isSticky={isSticky} />
            ) : (
              <TabsV3
                className={cx(toolkitCx.layout.transition.transform, {
                  "translate-y-10": isStickyOnLargeScreen,
                  "translate-y-0": !isStickyOnLargeScreen,
                })}
                withBorder={false}
                value={tabValue}
                size="lg"
                onValueChange={handleTabChange}
                data={[
                  {
                    id: "widget",
                    content: memoizedWidgetNode,
                    trigger: (
                      <div
                        style={{
                          color: tasteTestPersonalization?.primaryColor || defaultPrimaryColor,
                        }}
                      >
                        <Text
                          tag="span"
                          size="lg"
                          variant={
                            tabValue !== "widget"
                              ? "normal"
                              : tasteTestPersonalization?.primaryColor
                              ? "current"
                              : "info"
                          }
                        >
                          {memoizedTransactionTabTriggerLabel}
                        </Text>
                      </div>
                    ),
                  },
                  {
                    id: "contributionsTable",
                    content: (
                      <TasteTestContributionsTable
                        isSticky={isStickyOnLargeScreen}
                        onClickContribute={() => handleTabChange("widget")}
                      />
                    ),
                    trigger: (
                      <div
                        style={{
                          color: tasteTestPersonalization?.primaryColor || defaultPrimaryColor,
                        }}
                      >
                        <Text
                          tag="span"
                          size="lg"
                          variant={
                            tabValue !== "contributionsTable"
                              ? "normal"
                              : tasteTestPersonalization?.primaryColor
                              ? "current"
                              : "info"
                          }
                        >
                          {t("detail.contributionsTab.table")}
                        </Text>
                      </div>
                    ),
                  },
                ]}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(TasteTestTransactionWidget);
